/**
 * Picking several routes at once, the way a file list does: a plain click takes
 * one, ⌘/Ctrl adds it or drops it, Shift takes everything between the last
 * plain click and this one.
 *
 * Kept apart from the columns because it is the one piece of this page with
 * rules rather than markup — and because both lists in the routes column, the
 * sector's topo and the routes nobody has filed yet, go through it.
 */

export interface RouteSelection {
  ids: string[];
  /** Where a Shift-click measures from: the last row clicked without one. */
  anchorId: string | null;
}

export const EMPTY_SELECTION: RouteSelection = { ids: [], anchorId: null };

export interface SelectionClick {
  routeId: string;
  /** ⌘ on a Mac, Ctrl everywhere else. */
  additive: boolean;
  /** Shift. */
  range: boolean;
  /** The ids of the list the row sits in, in the order they are drawn. */
  order: string[];
}

/**
 * A range never crosses from the sector's topo into the unfiled routes or back:
 * `order` is one list, and a Shift-click that swallowed the gap between the two
 * would pick up routes the eye never travelled over.
 */
export function selectionAfterClick(current: RouteSelection, click: SelectionClick): RouteSelection {
  const { routeId, additive, range, order } = click;

  if (range && current.anchorId !== null) {
    const from = order.indexOf(current.anchorId);
    const to = order.indexOf(routeId);

    if (from !== -1 && to !== -1) {
      const [start, end] = from <= to ? [from, to] : [to, from];
      // The anchor stays put, so dragging the Shift-click back and forth grows
      // and shrinks one range rather than leaving a trail of them.
      return { ids: order.slice(start, end + 1), anchorId: current.anchorId };
    }
  }

  if (additive) {
    const ids = current.ids.includes(routeId)
      ? current.ids.filter((id) => id !== routeId)
      : [...current.ids, routeId];

    return { ids, anchorId: routeId };
  }

  return { ids: [routeId], anchorId: routeId };
}

/**
 * What a drag carries: the whole selection when the card picked up is part of
 * it, that card alone otherwise — dragging something outside the selection is
 * how every file manager starts a new one.
 */
export function routesToMove(selection: RouteSelection, draggedId: string): string[] {
  return selection.ids.includes(draggedId) ? selection.ids : [draggedId];
}
